import type { KeyboardEvent } from 'react'

import { Button, type ButtonVariant } from '@/components/button'
import { FormError } from '@/components/form-error'
import { type IdentifierDraft, identifierTyped } from '@/features/auth/identifier-draft'
import { IdentifierInput } from '@/features/auth/identifier-input'
import { useT } from '@/lib/i18n/translate'

/** The exact-match account search (E7, H8, platform superuser): identifier, its error, Search. Enter searches. */
export function IdentifierSearch({
  draft,
  onDraftChange,
  onSearch,
  pending,
  error,
  required = true,
  variant = 'primary',
}: {
  draft: IdentifierDraft
  onDraftChange: (draft: IdentifierDraft) => void
  onSearch: () => void
  pending: boolean
  error: unknown
  /** Off when the identifier is an optional part of a larger form. */
  required?: boolean
  variant?: ButtonVariant
}) {
  const t = useT()
  const typed = identifierTyped(draft)

  function search() {
    if (typed && !pending) onSearch()
  }

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key !== 'Enter' || !(event.target instanceof HTMLInputElement)) return
    event.preventDefault()
    search()
  }

  return (
    <div className="flex flex-col gap-4" onKeyDown={onKeyDown}>
      <IdentifierInput value={draft} onChange={onDraftChange} required={required} />
      <FormError error={error} />
      <Button type="button" variant={variant} loading={pending} disabled={!typed} className="self-start" onClick={search}>
        {t('accounts.search')}
      </Button>
    </div>
  )
}
